import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import { FeatureFlagApi } from 'lib/api/feature-flag';
import { FeatureFlagDTO } from 'lib/api/feature-flag.protocols';
import {
  SETTINGS_MODULES,
  FLAG_DISPLAY,
} from 'modules/settings/config/modules.config';
import { brand } from 'themes/brand';

const MODULE_KEY = 'solicitacoes';

export const SettingsModuleDetailPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const moduleConfig = SETTINGS_MODULES.find((m) => m.moduleKey === MODULE_KEY);

  const [flags, setFlags] = useState<FeatureFlagDTO[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [togglingKey, setTogglingKey] = useState<string | null>(null);

  const fetchFlags = useCallback(() => {
    setLoadError(false);
    setFlags(null);
    FeatureFlagApi.getFeatureFlags()
      .then(setFlags)
      .catch(() => setLoadError(true));
  }, []);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  if (!moduleConfig) {
    return null;
  }

  const moduleFlags = (flags ?? []).filter((flag) =>
    moduleConfig.flagKeys.includes(flag.key),
  );

  const handleToggle = async (flag: FeatureFlagDTO) => {
    const nextEnabled = !flag.enabled;
    const previousEnabled = flag.enabled;
    const title = FLAG_DISPLAY[flag.key]?.title ?? flag.key;

    setFlags((prev) =>
      (prev ?? []).map((f) =>
        f.key === flag.key ? { ...f, enabled: nextEnabled } : f,
      ),
    );
    setTogglingKey(flag.key);

    try {
      await FeatureFlagApi.toggleFeatureFlag(flag.key, {
        enabled: nextEnabled,
      });
      enqueueSnackbar(
        `'${title}' foi ${nextEnabled ? 'ativada' : 'desativada'}`,
        {
          variant: 'success',
          autoHideDuration: 6000,
        },
      );
    } catch (err) {
      setFlags((prev) =>
        (prev ?? []).map((f) =>
          f.key === flag.key ? { ...f, enabled: previousEnabled } : f,
        ),
      );
      enqueueSnackbar(
        'Não foi possível atualizar a funcionalidade. Tente novamente.',
        {
          variant: 'error',
          autoHideDuration: 6000,
        },
      );
    } finally {
      setTogglingKey(null);
    }
  };

  const Icon = moduleConfig.icon;

  return (
    <Container sx={{ my: 2 }}>
      <Breadcrumbs separator="›" sx={{ mb: 2 }}>
        <Link
          component="button"
          onClick={() => navigate('/configuracoes')}
          underline="hover"
        >
          Configurações
        </Link>
        <Typography color="text.primary">{moduleConfig.label}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2.5,
            bgcolor: brand.tint,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ color: brand.primary, opacity: 0.85 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {moduleConfig.label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ative ou desative funcionalidades relacionadas à criação de
            solicitações. As alterações têm efeito imediato para todos os
            usuários.
          </Typography>
        </Box>
      </Box>

      <Alert severity="warning" sx={{ mb: 3 }}>
        <strong>Atenção:</strong> desativar uma funcionalidade em produção afeta
        imediatamente os analistas que estiverem utilizando o recurso. Considere
        avisar as equipes antes de alterar.
      </Alert>

      {flags === null && !loadError && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Carregando funcionalidades...
          </Typography>
        </Box>
      )}

      {loadError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchFlags}>
              Tentar novamente
            </Button>
          }
        >
          Não foi possível carregar as funcionalidades. Tente novamente mais
          tarde.
        </Alert>
      )}

      {flags !== null && !loadError && (
        <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 1.5, bgcolor: brand.surface }}>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={600}
            >
              Funcionalidades
            </Typography>
          </Box>
          <Divider />
          {moduleFlags.map((flag, index) => {
            const display = FLAG_DISPLAY[flag.key];

            return (
              <Box key={flag.key}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 3,
                    py: 2,
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight={600}>
                        {display?.title ?? flag.key}
                      </Typography>
                      <Chip
                        size="small"
                        label={flag.enabled ? 'ATIVADO' : 'DESATIVADO'}
                        sx={{
                          bgcolor: flag.enabled
                            ? brand.status.success.bg
                            : 'grey.100',
                          color: flag.enabled
                            ? brand.status.success.fg
                            : 'text.secondary',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {display?.helperText ?? flag.description}
                    </Typography>
                  </Box>
                  <Switch
                    checked={flag.enabled}
                    disabled={togglingKey === flag.key}
                    onChange={() => handleToggle(flag)}
                    color="primary"
                  />
                </Box>
                {index < moduleFlags.length - 1 && <Divider />}
              </Box>
            );
          })}
        </Card>
      )}
    </Container>
  );
};
